import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ref, reactive, onMounted, nextTick } from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { useRoute } from 'vue-router'
import PageSubscribeBar from '@/views/dashboard/_components/subscribe-bar/page-subscribe-bar.vue'
import Card from '@/views/dashboard/_components/card/card.vue'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import StatisticsCards from './_components/statistics-cards.vue'
import PortTypeDistributionChart from './_components/port-type-distribution-chart.vue'
import RiskPortRankingChart from './_components/risk-port-ranking-chart.vue'
import PortBlockDrawer from './_components/port-view/_components/port-block-drawer.vue'
import BlockViewDrawer from './_components/block-view/_components/block-view-drawer.vue'
import TerminalListTop10 from './_components/terminal-list-top10/terminal-list-top10.vue'
import PortListTop10 from './_components/port-list-top10/port-list-top10.vue'
import BlockedPortList from './_components/blocked-port-list/blocked-port-list.vue'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'
import { PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { PortManageService, PortItem } from './port-manage.service'
import type { PortTypeSliceFilter } from './_components/port-type-distribution-chart.vue'
import { PortViewService, QueryParams as PortQueryParams, PortItem as PortViewItem } from './_components/port-view/port-view.service'
import { BlockViewService, QueryParams as BlockQueryParams, BlockPortItem } from './_components/block-view/block-view.service'
import { TerminalViewService } from './_components/terminal-view/terminal-view.service'
import type { PortManageStatCardKey } from './_components/port-manage-statistics-cards.types'


export default /*#__PURE__*/_defineComponent({
  __name: 'port-manage',
  setup(__props) {

const injector = useReflectiveInjector([
  PortManageService,
  PortViewService,
  BlockViewService,
  TerminalViewService
])
const portManageService = injector.get(PortManageService)
const portViewService = injector.get(PortViewService)
const blockViewService = injector.get(BlockViewService)
const servicePermissionService = injector.get(ServicePermissionService)

const hasPermission = servicePermissionService.getHasPermission(PermissionFe.port_control)
const route = useRoute()

// --- Statistics Data ---
const statistics = reactive({
  riskPorts: 0,
  blockedPorts: 0
})

// --- Chart Data ---
const portTypeDistributionData = ref<{ risk_port_count: number; unknown_port_count: number } | undefined>(undefined)

const riskPortRankingData = ref<PortItem[]>([])

// --- Component Refs ---
const terminalListTop10Ref = ref()
const portListTop10Ref = ref()
const blockedPortListRef = ref()
const riskPortRankingChartRef = ref()

// --- Drawer States ---
const portDrawer = ref(false)
const currentPort = ref<PortViewItem | null>(null)
const resetBlockPortDrawer = ref(false)
const currentBlockPort = ref<BlockPortItem | null>(null)
const terminalDrawerVisible = ref(false)
const portDrawerVisible = ref(false)
/** 从饼图进入「全部端口」抽屉时预选的 risk 筛选（1 风险 / 2 未知），否则为 undefined */
const portDrawerInitialIsRiskPort = ref<1 | 2 | undefined>(undefined)
/** 风险 TOP5 / 搜索：端口号关键字 */
const portDrawerInitialKeyword = ref('')
const portDrawerKey = ref(0)
const blockDrawerVisible = ref(false)

function getSystemTypeLabel(systemType: number): string {
  if (systemType === 1) return 'Windows'
  if (systemType === 2) return 'Linux'
  if (systemType === 3) return 'WinServer'
  return '--'
}

function viewAllTerminals() {
  if (!hasPermission) return
  terminalDrawerVisible.value = true
}

function viewAllPorts() {
  if (!hasPermission) return
  portDrawerInitialIsRiskPort.value = undefined
  portDrawerInitialKeyword.value = ''
  portDrawerKey.value += 1
  portDrawerVisible.value = true
}

function openPortListDrawerFromPie(payload: PortTypeSliceFilter) {
  portDrawerInitialIsRiskPort.value = payload.is_risk_port
  portDrawerInitialKeyword.value = ''
  portDrawerKey.value += 1
  portDrawerVisible.value = true
}

function rankingPortItemToViewPort(row: PortItem): PortViewItem {
  return {
    port_num: row.port_num,
    protocol: row.protocol ?? '',
    process_name: row.process_name ?? '',
    bind_ip: row.bind_ip,
    device_count: row.device_count,
    is_known_risk: row.is_known_risk,
    risk_detail: row.risk_detail,
    system_type: row.system_type
  }
}

/** 风险 TOP5 行点击：直接打开端口阻断抽屉（与列表「阻断」一致） */
function openPortBlockDrawerFromRiskRanking(payload: { port_num: number }) {
  const row = riskPortRankingData.value.find(p => p.port_num === payload.port_num)
  if (!row) return
  handleBlockPort(rankingPortItemToViewPort(row))
}

/** 顶栏统计卡：风险端口 / 已阻断端口（statistics-events） */
function handlePortManageStatClick(key: PortManageStatCardKey) {
  if (key === 'riskPorts') {
    if (statistics.riskPorts <= 0) return
    portDrawerInitialIsRiskPort.value = 1
    portDrawerInitialKeyword.value = ''
    portDrawerKey.value += 1
    portDrawerVisible.value = true
    return
  }
  if (statistics.blockedPorts <= 0) return
  viewAllBlockedPorts()
}

function handleBlockPort(row: PortViewItem) {
  currentPort.value = row
  portDrawer.value = true
}

function handleBatchBlock() {
  // TODO: Implement batch block
  console.log('Batch block ports')
}


function handleUnblockPort(row: BlockPortItem) {
  currentBlockPort.value = row
  resetBlockPortDrawer.value = true
}

function viewAllBlockedPorts() {
  if (!hasPermission) return
  blockDrawerVisible.value = true
}

function handlePortBlockDrawerClose() {
  portDrawer.value = false
}

function closeDrawerAndRefresh() {
  portListTop10Ref.value?.getList()
  portDrawer.value = false
}

function handleResetBlockPortDrawerClose() {
  resetBlockPortDrawer.value = false
}

function closeResetBlockDrawerAndRefresh() {
  blockedPortListRef.value?.getList()
  resetBlockPortDrawer.value = false
}


// 加载统计数据
async function loadStatistics() {
  if (!hasPermission) return

  try {
    const res = await portManageService.getPortcontrolPortStat()
    statistics.blockedPorts = res.blocked_port_count || 0
    statistics.riskPorts = res.risk_port_count || 0
  } catch (error) {
    console.error('加载统计数据失败:', error)
  }
}

// 加载端口类型分布数据
async function loadPortTypeDistribution() {
  if (!hasPermission) return

  try {
    const res = await portManageService.getPortcontrolPortTypeStat()
    portTypeDistributionData.value = {
      risk_port_count: res.risk_port_count || 0,
      unknown_port_count: res.unknown_port_count || 0
    }
  } catch (error) {
    console.error('加载端口类型分布数据失败:', error)
    portTypeDistributionData.value = undefined
  }
}

// 加载风险端口排行数据
async function loadRiskPortRanking() {
  if (!hasPermission) return

  if (riskPortRankingChartRef.value) {
    riskPortRankingChartRef.value.loading = true
  }
  try {
    const res = await portManageService.getRiskPortList({
      page: 1,
      limit: 5,
      asc: false,
      sort: 'device_count'
    })
    riskPortRankingData.value = res.port_list || []
  } catch (error) {
    console.error('加载风险端口排行数据失败:', error)
    riskPortRankingData.value = []
  } finally {
    if (riskPortRankingChartRef.value) {
      riskPortRankingChartRef.value.loading = false
    }
  }
}

onMounted(async () => {
  if (hasPermission) {
    await nextTick()
    loadStatistics()
    loadPortTypeDistribution()
    loadRiskPortRanking()
    if (String(route.query.open_port_list_drawer || '') === '1') {
      viewAllPorts()
    }
  }
})

return (_ctx: any,_cache: any) => {
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PageSubscribeBar, {
      "normal-permission-key": _unref(PermissionFe).port_control
    }, null, 8, ["normal-permission-key"]),
    _createVNode(StatisticsCards, {
      "risk-ports": statistics.riskPorts,
      "blocked-ports": statistics.blockedPorts,
      onStatClick: handlePortManageStatClick
    }, null, 8, ["risk-ports", "blocked-ports"]),
    _createVNode(_component_el_row, {
      gutter: 16,
      class: "cm-ui-mb16",
      style: {"height":"360px"}
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, { span: 12 }, {
          default: _withCtx(() => [
            _createVNode(PortTypeDistributionChart, {
              "raw-data": portTypeDistributionData.value,
              onSliceClick: openPortListDrawerFromPie
            }, null, 8, ["raw-data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 12 }, {
          default: _withCtx(() => [
            _createVNode(RiskPortRankingChart, {
              ref_key: "riskPortRankingChartRef",
              ref: riskPortRankingChartRef,
              data: riskPortRankingData.value,
              onRowClick: openPortBlockDrawerFromRiskRanking
            }, null, 8, ["data"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('portManage.portListTop10'),
      class: "cm-ui-mb16"
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(PortListTop10, {
          ref_key: "portListTop10Ref",
          ref: portListTop10Ref,
          onViewAll: viewAllPorts,
          onBlockPort: handleBlockPort
        }, null, 512)
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('portManage.terminalListTop10'),
      class: "cm-ui-mb16"
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(TerminalListTop10, {
          ref_key: "terminalListTop10Ref",
          ref: terminalListTop10Ref,
          onViewAll: viewAllTerminals
        }, null, 512)
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Card, {
      size: "second",
      title: _ctx.$t('portManage.blockedPortList')
    }, {
      default: _withCtx(() => [
        _createVNode(BlockedPortList, {
          ref_key: "blockedPortListRef",
          ref: blockedPortListRef,
          onViewAll: viewAllBlockedPorts,
          onUnblockPort: handleUnblockPort
        }, null, 512)
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(_component_el_drawer, {
      size: "60%",
      modelValue: portDrawer.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((portDrawer).value = $event)),
      direction: "rtl",
      title: _ctx.$t('portManage.blockPortDrawer'),
      "before-close": handlePortBlockDrawerClose,
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(PortBlockDrawer, {
          "current-port": currentPort.value,
          onClosePortTerminal: closeDrawerAndRefresh
        }, null, 8, ["current-port"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_component_el_drawer, {
      size: "60%",
      modelValue: resetBlockPortDrawer.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((resetBlockPortDrawer).value = $event)),
      direction: "rtl",
      title: _ctx.$t('portManage.unblockDrawer'),
      "before-close": handleResetBlockPortDrawerClose,
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(BlockViewDrawer, {
          "current-port": currentBlockPort.value,
          "is-reset": true,
          onClosePortTerminal: closeResetBlockDrawerAndRefresh
        }, null, 8, ["current-port"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('portManage.allTerminalList'),
      modelValue: terminalDrawerVisible.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((terminalDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(TerminalListTop10, {
          viewMode: _unref(TableViewMode).full,
          style: {"padding":"10px 16px"}
        }, null, 8, ["viewMode"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('portManage.allPortList'),
      modelValue: portDrawerVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((portDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(PortListTop10, {
          key: portDrawerKey.value,
          viewMode: _unref(TableViewMode).full,
          "initial-is-risk-port": portDrawerInitialIsRiskPort.value,
          "initial-port-keyword": portDrawerInitialKeyword.value,
          style: {"padding":"10px 16px"},
          onBlockPort: handleBlockPort
        }, null, 8, ["viewMode", "initial-is-risk-port", "initial-port-keyword"]))
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_component_el_drawer, {
      size: "80%",
      title: _ctx.$t('portManage.allBlockedPortList'),
      modelValue: blockDrawerVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((blockDrawerVisible).value = $event)),
      "destroy-on-close": ""
    }, {
      default: _withCtx(() => [
        _createVNode(BlockedPortList, {
          viewMode: _unref(TableViewMode).full,
          style: {"padding":"10px 16px"},
          onUnblockPort: handleUnblockPort
        }, null, 8, ["viewMode"])
      ]),
      _: 1
    }, 8, ["title", "modelValue"])
  ], 64))
}
}

})